import React from "react";
import {
  AbilityWrapper,
  AbilityVotingCards,
  AbilityText,
  AbilityWarning,
  InformationText,
  PhaseTag,
} from "../styles/Game.styles";
import VotingCard from "./VotingCard";
import { Header } from "../../../styleguide/atoms";

export default function NightActions({
  me,
  nightDone,
  nightTotal,
  othersAlive,
  // selections/flags
  hasWitchKey,
  myWitchPeek,
  myMathleteCheck,
  hasLifeguardKey,
  myLifeguardProtect,
  hasBeastKey,
  myBeastTarget,
  hasSkaterKey,
  mySkaterProtect,
  hasPunkKey,
  myPunkSilence,
  myNightKill,
  // handlers
  onWitchPeek,
  onMathCheck,
  onLifeguard,
  onBeast,
  onSkater,
  onPunk,
  onVillainVote,
  nightIndex,
  hasNurseKey,
  myNurseTarget,
  hasQuietKey,
  myQuietTarget,
  onNurse,
  onQuiet,
  onCounselorPair,
  myCounselorTargets,
  onDebatePair,
  onShadowBond,
  hasDebateKey,
  myDebateTargets,
  hasCheerKey,
  myCheerTarget,
  onCheer,
  hasChessKey,
  myChessTargets,
  onChessPair,
  onFanRoot,
  onHermit,
  myGossipTargets,
  onGossipPair,
  timeLeftMs,
  villainVoteCounts,
  villainsCount,
}) {
  const [ccPair, setCcPair] = React.useState([]);
  const [debatePair, setDebatePair] = React.useState([]);
  const [chessPair, setChessPair] = React.useState([]);
  const [gossipPair, setGossipPair] = React.useState([]);

  // Shared loading for any submitted action in this block
  const [submitting, setSubmitting] = React.useState(false);
  const [pendingKey, setPendingKey] = React.useState(null);
  const wrap =
    (fn, key) =>
    async (...args) => {
      if (submitting) return;
      setSubmitting(true);
      setPendingKey(key);
      try {
        await fn(...args);
      } catch (e) {
        console.error(e);
      } finally {
        setSubmitting(false);
        setPendingKey(null);
      }
    };

  // Pair helpers: submit only when 2 are selected
  function autoPairToggle(setCurrent, id, onSubmitWrapped) {
    setCurrent((prev) => {
      let next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length < 2
        ? [...prev, id]
        : prev;
      if (next.length === 2) {
        onSubmitWrapped(next);
      }
      return next;
    });
  }
  const onCounselorClick = (id) =>
    autoPairToggle(setCcPair, id, wrap(onCounselorPair, "counselor:submit"));
  const onDebateClick = (id) =>
    autoPairToggle(setDebatePair, id, wrap(onDebatePair, "debate:submit"));

  const showWitch = me?.roleTitle === "Witch of the Woods" && !hasWitchKey;
  const isAlive = me?.alive !== false;
  return (
    <>
      <PhaseTag>Night</PhaseTag>
      <InformationText>
        Waiting on players:{" "}
        <b>
          {nightDone} / {nightTotal}
        </b>{" "}
        acted
      </InformationText>
      <Header type="h3" align="center" color="white">
        {timeLeftMs == null
          ? ""
          : Math.max(0, Math.ceil(timeLeftMs / 1000)) + "s"}
      </Header>

      {isAlive ? (
        <>
          {me?.team === "Villain" && (
            <AbilityWrapper>
              <AbilityText>Kill a player.</AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={myNightKill === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "kill:skip"}
                  onClick={() => wrap(onVillainVote, "kill:skip")(null)}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => {
                  const voteCount =
                    villainsCount >= 2 && villainVoteCounts
                      ? villainVoteCounts[p.id] || 0
                      : 0;

                  return (
                    <VotingCard
                      key={p.id}
                      selected={myNightKill === p.id}
                      disabled={submitting}
                      loading={submitting && pendingKey === `kill:${p.id}`}
                      onClick={() => wrap(onVillainVote, `kill:${p.id}`)(p.id)}
                      badgeCount={voteCount}
                    >
                      {p.name}
                    </VotingCard>
                  );
                })}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {showWitch && (
            <AbilityWrapper>
              <AbilityText>See a player's role.</AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={hasWitchKey && myWitchPeek === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "witch:skip"}
                  onClick={() => wrap(onWitchPeek, "witch:skip")(null)}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={myWitchPeek === p.id}
                    disabled={submitting}
                    loading={submitting && pendingKey === `witch:${p.id}`}
                    onClick={() => wrap(onWitchPeek, `witch:${p.id}`)(p.id)}
                  >
                    {p.name}
                  </VotingCard>
                ))}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}
          {me?.roleTitle === "Witch of the Woods" && hasWitchKey && (
            <InformationText>You’ve used your ability.</InformationText>
          )}

          {me?.roleTitle === "Mathlete" && (
            <AbilityWrapper>
              <AbilityText>See if a player is a camper.</AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={myMathleteCheck === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "math:skip"}
                  onClick={() => wrap(onMathCheck, "math:skip")(null)}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={myMathleteCheck === p.id}
                    disabled={submitting}
                    loading={submitting && pendingKey === `math:${p.id}`}
                    onClick={() => wrap(onMathCheck, `math:${p.id}`)(p.id)}
                  >
                    {p.name}
                  </VotingCard>
                ))}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "Lifeguard" && (
            <AbilityWrapper>
              <AbilityText>Protect a player tonight.</AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={hasLifeguardKey && myLifeguardProtect === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "lifeguard:skip"}
                  onClick={() => wrap(onLifeguard, "lifeguard:skip")(null)}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={myLifeguardProtect === p.id}
                    disabled={submitting}
                    loading={submitting && pendingKey === `lifeguard:${p.id}`}
                    onClick={() => wrap(onLifeguard, `lifeguard:${p.id}`)(p.id)}
                  >
                    {p.name}
                  </VotingCard>
                ))}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "Woodland Beast" &&
            !(me?.meta && me.meta.beastUsed) && (
              <AbilityWrapper>
                <AbilityText>
                  Once per game: Kill an additional player.
                </AbilityText>
                <AbilityVotingCards>
                  <VotingCard
                    selected={hasBeastKey && myBeastTarget === null}
                    disabled={submitting}
                    loading={submitting && pendingKey === "beast:skip"}
                    onClick={() => wrap(onBeast, "beast:skip")(null)}
                  >
                    Skip Tonight
                  </VotingCard>
                  {othersAlive.map((p) => (
                    <VotingCard
                      key={p.id}
                      selected={myBeastTarget === p.id}
                      disabled={submitting}
                      loading={submitting && pendingKey === `beast:${p.id}`}
                      onClick={() => wrap(onBeast, `beast:${p.id}`)(p.id)}
                    >
                      {p.name}
                    </VotingCard>
                  ))}
                </AbilityVotingCards>
              </AbilityWrapper>
            )}

          {me?.roleTitle === "Skater" && (
            <AbilityWrapper>
              <AbilityText>Remove a player from tomorrow's vote.</AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={hasSkaterKey && mySkaterProtect === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "skater:skip"}
                  onClick={() => wrap(onSkater, "skater:skip")(null)}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={mySkaterProtect === p.id}
                    disabled={submitting}
                    loading={submitting && pendingKey === `skater:${p.id}`}
                    onClick={() => wrap(onSkater, `skater:${p.id}`)(p.id)}
                  >
                    {p.name}
                  </VotingCard>
                ))}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "Punk" && (
            <AbilityWrapper>
              <AbilityText>Silence a player from voting tomorrow.</AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={hasPunkKey && myPunkSilence === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "punk:skip"}
                  onClick={() => wrap(onPunk, "punk:skip")(null)}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={myPunkSilence === p.id}
                    disabled={submitting}
                    loading={submitting && pendingKey === `punk:${p.id}`}
                    onClick={() => wrap(onPunk, `punk:${p.id}`)(p.id)}
                  >
                    {p.name}
                  </VotingCard>
                ))}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "Cheerleader" && (
            <AbilityWrapper>
              <AbilityText>Increase another player's vote.</AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={hasCheerKey && myCheerTarget === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "cheer:skip"}
                  onClick={() => wrap(onCheer, "cheer:skip")(null)}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={myCheerTarget === p.id}
                    disabled={submitting}
                    loading={submitting && pendingKey === `cheer:${p.id}`}
                    onClick={() => wrap(onCheer, `cheer:${p.id}`)(p.id)}
                  >
                    {p.name}
                  </VotingCard>
                ))}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "Gossip" && (
            <AbilityWrapper>
              <AbilityText>
                Select two players to learn one of their roles.
              </AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={myGossipTargets === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "gossip:skip"}
                  onClick={() => {
                    setGossipPair([]);
                    return wrap(onGossipPair, "gossip:skip")(null);
                  }}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={gossipPair.includes(p.id)}
                    disabled={submitting}
                    onClick={() =>
                      autoPairToggle(
                        setGossipPair,
                        p.id,
                        wrap(onGossipPair, "gossip:submit")
                      )
                    }
                  >
                    {p.name}
                  </VotingCard>
                ))}
                {gossipPair.length === 1 ? (
                  <AbilityWarning>
                    Select a second player to use ability.
                  </AbilityWarning>
                ) : null}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "Chess Player" && (
            <AbilityWrapper>
              <AbilityText>
                Select two players to potential learn a role.
              </AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={hasChessKey && myChessTargets === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "chess:skip"}
                  onClick={() => {
                    setChessPair([]);
                    return wrap(onChessPair, "chess:skip")(null);
                  }}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={chessPair.includes(p.id)}
                    disabled={submitting}
                    onClick={() =>
                      autoPairToggle(
                        setChessPair,
                        p.id,
                        wrap(onChessPair, "chess:submit")
                      )
                    }
                  >
                    {p.name}
                  </VotingCard>
                ))}
                {chessPair.length === 1 ? (
                  <AbilityWarning>
                    Select a second player to use ability.
                  </AbilityWarning>
                ) : null}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "Camp Counselor" && (
            <AbilityWrapper>
              <AbilityText>Select two players to compare.</AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={myCounselorTargets === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "counselor:skip"}
                  onClick={() => {
                    setCcPair([]);
                    return wrap(onCounselorPair, "counselor:skip")(null);
                  }}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={ccPair.includes(p.id)}
                    disabled={submitting}
                    onClick={() => onCounselorClick(p.id)}
                  >
                    {p.name}
                  </VotingCard>
                ))}
                {ccPair.length === 1 ? (
                  <AbilityWarning>
                    Select a second player to use ability.
                  </AbilityWarning>
                ) : null}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "Debate Student" &&
            !(me?.meta && me.meta.debateUsed) && (
              <AbilityWrapper>
                <AbilityText>
                  Once per game: Select two players to narrow tomorrow’s vote.
                </AbilityText>
                <AbilityVotingCards>
                  <VotingCard
                    selected={hasDebateKey && myDebateTargets === null}
                    disabled={submitting}
                    loading={submitting && pendingKey === "debate:skip"}
                    onClick={() => {
                      setDebatePair([]);
                      return wrap(onDebatePair, "debate:skip")(null);
                    }}
                  >
                    Skip Tonight
                  </VotingCard>
                  {othersAlive.map((p) => (
                    <VotingCard
                      key={p.id}
                      selected={debatePair.includes(p.id)}
                      disabled={submitting}
                      onClick={() => onDebateClick(p.id)}
                    >
                      {p.name}
                    </VotingCard>
                  ))}
                  {debatePair.length === 1 ? (
                    <AbilityWarning>
                      Select a second player to use ability.
                    </AbilityWarning>
                  ) : null}
                </AbilityVotingCards>
              </AbilityWrapper>
            )}

          {me?.roleTitle === "Nurse" && (
            <AbilityWrapper>
              <AbilityText>Protect a player from tomorrow’s vote.</AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={hasNurseKey && myNurseTarget === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "nurse:skip"}
                  onClick={() => wrap(onNurse, "nurse:skip")(null)}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={myNurseTarget === p.id}
                    disabled={submitting}
                    loading={submitting && pendingKey === `nurse:${p.id}`}
                    onClick={() => wrap(onNurse, `nurse:${p.id}`)(p.id)}
                  >
                    {p.name}
                  </VotingCard>
                ))}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "Quiet Kid" && (
            <AbilityWrapper>
              <AbilityText>
                Select a player to potentially learn their role.
              </AbilityText>
              <AbilityVotingCards>
                <VotingCard
                  selected={hasQuietKey && myQuietTarget === null}
                  disabled={submitting}
                  loading={submitting && pendingKey === "quiet:skip"}
                  onClick={() => wrap(onQuiet, "quiet:skip")(null)}
                >
                  Skip
                </VotingCard>
                {othersAlive.map((p) => (
                  <VotingCard
                    key={p.id}
                    selected={myQuietTarget === p.id}
                    disabled={submitting}
                    loading={submitting && pendingKey === `quiet:${p.id}`}
                    onClick={() => wrap(onQuiet, `quiet:${p.id}`)(p.id)}
                  >
                    {p.name}
                  </VotingCard>
                ))}
              </AbilityVotingCards>
            </AbilityWrapper>
          )}

          {me?.roleTitle === "The Shadow" &&
            !me?.meta?.shadowBondTarget &&
            nightIndex === 1 && (
              <AbilityWrapper>
                <AbilityText>
                  Select a non-villain player. When you die, they die too.
                </AbilityText>
                <AbilityVotingCards>
                  {othersAlive
                    .filter((p) => p.team !== "Villain")
                    .map((p) => (
                      <VotingCard
                        key={p.id}
                        disabled={submitting}
                        loading={submitting && pendingKey === `shadow:${p.id}`}
                        onClick={() =>
                          wrap(onShadowBond, `shadow:${p.id}`)(p.id)
                        }
                      >
                        {p.name}
                      </VotingCard>
                    ))}
                </AbilityVotingCards>
              </AbilityWrapper>
            )}

          {me?.roleTitle === "Anime Fan" &&
            !me?.meta?.fanRootTarget &&
            nightIndex === 1 && (
              <AbilityWrapper>
                <AbilityText>Select a player to root for.</AbilityText>
                <AbilityVotingCards>
                  {othersAlive.map((p) => (
                    <VotingCard
                      key={p.id}
                      disabled={submitting}
                      loading={submitting && pendingKey === `fan:${p.id}`}
                      onClick={() => wrap(onFanRoot, `fan:${p.id}`)(p.id)}
                    >
                      {p.name}
                    </VotingCard>
                  ))}
                </AbilityVotingCards>
              </AbilityWrapper>
            )}

          {me?.roleTitle === "Hermit" &&
            !me?.meta?.hermitSabotageTarget &&
            nightIndex === 1 && (
              <AbilityWrapper>
                <AbilityText>Select a player to sabotage.</AbilityText>
                <AbilityVotingCards>
                  {othersAlive.map((p) => (
                    <VotingCard
                      key={p.id}
                      disabled={submitting}
                      loading={submitting && pendingKey === `hermit:${p.id}`}
                      onClick={() => wrap(onHermit, `hermit:${p.id}`)(p.id)}
                    >
                      {p.name}
                    </VotingCard>
                  ))}
                </AbilityVotingCards>
              </AbilityWrapper>
            )}
        </>
      ) : null}
    </>
  );
}
